import React, { useEffect, useRef, useState } from 'react';
import './AutoScrollCarousel.css';

/**
 * AutoScrollCarousel — горизонтально прокручивающийся carousel с медленной
 * auto-анимацией влево. Пользователь может перехватить контроль (touch /
 * wheel / drag) — auto-scroll paused; через `resumeDelay` секунд idle
 * без пользовательского input — auto-scroll возобновляется.
 *
 * Использует requestAnimationFrame для плавности + `scroll-snap` только
 * во время паузы (#1022 review fix C1): на лету snap=`mandatory` после
 * каждого write возвращает scrollLeft на 0 (даже proximity на Chrome
 * ловит integer pixel write как "near snap point"), поэтому во время
 * auto-scroll snap = `none`, при паузе восстанавливаем proximity для
 * comfortable manual swipe.
 *
 * Дробный аккумулятор позиции хранится в ref-е: scrollLeft — это
 * integer pixel в DOM, прямое присваивание `scrollLeft += 0.4` теряет
 * фракцию каждый кадр и движение никогда не виден на скорости < 1px/frame.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children — карточки внутри carousel.
 *   Каждая получит `.auto-scroll-carousel__item` обёртку с scroll-snap.
 * @param {number} [props.speed=0.4] — скорость auto-scroll, px / frame
 *   (~60fps). 0.4 px/frame = 24 px/s — медленно, но заметно.
 * @param {number} [props.resumeDelay=3000] — задержка возобновления
 *   auto-scroll после ручного взаимодействия, ms.
 * @param {string} [props.ariaLabel] — accessibility label.
 * @param {() => void} [props.onUserInteract] — фаер на первое ручное
 *   взаимодействие (touch/pointer/wheel). Используется SalonLandingPage для
 *   dismiss'а swipe-hint chevrons при первом пользовательском свайпе.
 * @param {string} [props.className]
 *
 * При `prefers-reduced-motion: reduce` авто-прокрутки нет вовсе — лента
 * листается только руками.
 */
export const AutoScrollCarousel = ({
  children,
  speed = 0.4,
  resumeDelay = 3000,
  ariaLabel,
  onUserInteract,
  className = '',
}) => {
  const scrollerRef = useRef(null);
  const rafRef = useRef(null);
  const accumRef = useRef(0);
  const resumeTimerRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Auto-scroll loop — стартует когда paused=false. На паузе useEffect
  // переинициализируется и cleanup'нет rAF.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || paused) return undefined;
    // Спокойное движение (#1456): лента не едет сама — то, что ползёт по
    // экрану без участия человека, ровно то, от чего эта настройка защищает.
    // Ручное листание (свайп, колесо, перетаскивание) остаётся.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    // Сбрасываем snap-type на none пока бежит auto-scroll: иначе snap
    // принудительно возвращает scrollLeft на 0 после каждого write
    // (наблюдено в Chrome 131+, см. #1022 review).
    const prevSnapType = scroller.style.scrollSnapType;
    scroller.style.scrollSnapType = 'none';

    // Sync аккумулятор с текущим (после ручного scroll) положением,
    // чтобы не «перепрыгивать» обратно.
    accumRef.current = scroller.scrollLeft;

    const tick = () => {
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      accumRef.current += speed;
      if (accumRef.current >= maxScroll - 1) {
        // Loop: возвращаемся в начало. Лёгкий «прыжок», без crossfade.
        // Для smoother loop можно дублировать элементы (TODO).
        accumRef.current = 0;
      }
      // scrollLeft принимает только integer px (округление в DOM); фракцию
      // храним в accumRef и присваиваем округлённое значение каждый кадр.
      scroller.scrollLeft = Math.round(accumRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Возвращаем proximity для удобного manual-свайпа на паузе.
      scroller.style.scrollSnapType = prevSnapType || '';
    };
  }, [paused, speed]);

  // Pause auto-scroll при ручном взаимодействии (touch + pointer + wheel).
  // Resume — через resumeDelay ms тишины. Wheel не имеет нативного
  // «wheel end» события, поэтому используем тот же debounce-подход
  // что и touch/pointer: каждое событие сбрасывает таймер. (#1022 review fix H2).
  const handleInteraction = () => {
    setPaused(true);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setPaused(false), resumeDelay);
    onUserInteract?.();
  };

  useEffect(() => () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
  }, []);

  // React.Children.toArray сохраняет key каждого ребёнка, чтобы wrapper-div
  // мог их пробросить и не использовать array index (анти-паттерн при
  // динамическом списке машин). (#1022 review fix M4).
  const items = React.Children.toArray(children);

  // Infinite wrap-around (#1022 — Маша «после последней машины должна
  // листаться первая»). Дублируем список ×2; когда manual scroll или
  // auto-scroll переходит границу первой половины, мгновенно (без анимации)
  // подмещаем scrollLeft назад на половину — пользователь визуально
  // приземляется на ту же first car без видимой склейки. Если в инвентори
  // <2 машин — дублирование пропускаем (нечего зацикливать). */
  const loopable = items.length > 1;
  const renderItems = loopable ? [...items, ...items] : items;

  const handleScrollWrap = () => {
    const scroller = scrollerRef.current;
    if (!scroller || !loopable) return;
    const half = scroller.scrollWidth / 2;
    if (scroller.scrollLeft >= half) {
      // Silent jump — без smooth-behavior; user не видит «перепрыг»
      // потому что first card второй половины в той же позиции что
      // first card первой половины.
      scroller.scrollLeft -= half;
      // Sync accum чтобы auto-scroll не «откатил» назад на следующем кадре.
      accumRef.current = scroller.scrollLeft;
    }
  };

  return (
    <div
      ref={scrollerRef}
      className={`auto-scroll-carousel ${className}`}
      role="region"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
      onPointerDown={handleInteraction}
      onTouchStart={handleInteraction}
      onWheel={handleInteraction}
      onScroll={handleScrollWrap}
    >
      {renderItems.map((child, idx) => (
        <div
          key={`${child.key ?? idx}-${idx < items.length ? 'a' : 'b'}`}
          className="auto-scroll-carousel__item"
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default AutoScrollCarousel;
